import { useState } from "react";
import WishlistButton from "./WishlistButton";
import { Plus, Check } from "lucide-react";
import { useCart } from "../context/CartContext";
import Banner from "./Banner";

const ProductCard = ({
  product,
  index,
  onView,
  isBuildMode = false,
  onBuildAdd,
}) => {
  const [banner, setBanner] = useState(null);
  const {
    selectedFlowers,
    selectedAccessories,
    selectedVips,
    addFlower,
    addAccessory,
    addVip,
    removeFlower,
    removeAccessory,
    removeVip,
  } = useCart();


  const isFlower = product.category === "flower";
  const isAccessory = product.category === "accessory";
  const isVipProduct = product.category === "vip";

  const isSelected =
    isFlower
      ? selectedFlowers.some((item) => item._id === product._id)
      : isAccessory
        ? selectedAccessories.some((item) => item._id === product._id)
        : isVipProduct
          ? selectedVips.some((item) => item._id === product._id)
          : false;

  const handleAdd = (e) => {
    e.stopPropagation();
    if (!product.inStock) return;

    // ---------------- BUILD MODE ----------------
    if (isBuildMode && onBuildAdd) {
      onBuildAdd(product);
      return; // build mode handles everything
    }

    // ---------------- NORMAL SHOPPING ----------------
    if (isFlower) {
      const flowerSelected = selectedFlowers.some(f => f._id === product._id);
      if (flowerSelected) {
        removeFlower(product._id);
      } else {
        addFlower(product);
      }
      return;
    }

    if (isAccessory) {
      const accessorySelected = selectedAccessories.some(a => a._id === product._id);
      accessorySelected ? removeAccessory(product._id) : addAccessory(product);
      return;
    }

    if (isVipProduct) {
      const vipSelected = selectedVips.some(v => v._id === product._id);
      vipSelected ? removeVip(product._id) : addVip(product);
      return;
    }
  };

  return (
    <>
      <div
        className="bg-white shadow rounded-lg overflow-hidden hover:shadow-md transition cursor-pointer"
        onClick={() => {
          if (onView) onView(product);
        }}
        data-aos="zoom-in"
        data-aos-delay={index * 100}
        data-accessory-type={product.gender}
      >
        {/* Image & VIP Tag */}
        <div className="relative">
          <img
            src={product.image}
            alt={`Product: ${product.name}`}
            className="w-full h-48 object-cover rounded-t-lg"
          />
          {product.category === "vip" && (
            <span className="absolute top-2 left-2 bg-yellow-300 text-yellow-900 px-2 py-0.5 rounded-full text-xs shadow">
              VIP
            </span>
          )}
          <WishlistButton
            product={product}
            onFeedback={(type, msg) =>
              setBanner({ type, message: msg })
            }
          />
        </div>

        {/* Product Details */}
        <div className="p-4">
          <h3 className="font-semibold text-lg leading-snug line-clamp-2 min-h-[2.6em] mb-1">
            {product.name}
          </h3>

          <p className="text-sm text-gray-600 mb-2 line-clamp-2 min-h-[2.6em]">
            {product.description ||
              "Stylish, thoughtful, and perfect for gift pairings."}
          </p>

          <div className="flex justify-between items-center">
            <span className="text-blue-600 font-bold">
              ${Number(product.price).toLocaleString(undefined, {
                minimumFractionDigits: 2,
              })}
            </span>

            <button
              disabled={product.inStock === false}
              className={`text-sm px-3 py-1 rounded transition flex items-center gap-1 ${
                product.inStock === false
                  ? "bg-gray-400 text-white cursor-not-allowed"
                  : isSelected
                  ? "bg-green-600 text-white"
                  : "bg-blue-600 text-white hover:bg-blue-700"
              }`}
              onClick={handleAdd}
            >
              {product.inStock === false ? (
                "Out of Stock"
              ) : isSelected ? (
                <>
                  <Check size={16} /> Added
                </>
              ) : (
                <>
                  <Plus size={16} /> Add
                </>
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Banner Notification */}
      {banner && (
        <Banner
          type={banner.type}
          message={banner.message}
        />
      )}
    </>
  );
};

export default ProductCard;
